import {EstimateTreeDto} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeOperationDto} from '../../../shared/dtos/estimate-tree-operation.dto';
import {EstimateTreeEquipmentDto} from '../../../shared/dtos/estimate-tree-equipment.dto';
import {EquipmentChargeRow, equipmentCharges, lockedCost} from '../../../shared/pricing/equipment-charges';

/*
 * The estimate record's numbers as Android works them out (TimeDisplay.kt,
 * WorkItemsController.kt, core/pricing/PriceDistribution.kt). Price is the
 * number of record; stored operation minutes are TOTAL labor (the crew folded
 * in), and every time on the page is wall-clock: labor ÷ crew, rounded so the
 * parts add up to the whole.
 */

const usd = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'});

/** Always with cents, as the record shows money ("$1,234.00"); "—" for nothing. */
export const money = (n?: number | null) => n == null ? '—' : usd.format(n);

/** "45m", "2h", "1h 5m" (TimeDisplay.kt formatMinutes). */
export const formatMinutes = (minutes: number) => {
  const whole = Math.max(0, Math.round(minutes));
  const h = Math.floor(whole / 60);
  const m = whole - h * 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

/**
 * The labor minutes the steppers and totals act on (WorkItemsController
 * .effectiveMinutes): the override, the stored minutes, else, for an
 * operation from before minutes were stored, its price at the crew's rate.
 */
export const laborMinutes = (op: EstimateTreeOperationDto, rate?: number | null): number => {
  if (op.estimatedMinutesOverride != null) return op.estimatedMinutesOverride;
  if (op.estimatedMinutes != null) return op.estimatedMinutes;
  const cost = op.estimatedCostOverride ?? op.estimatedCost;
  if (cost != null && rate && rate > 0) return Math.round(cost / rate * 60);
  return 0;
};

export const treeLaborMinutes = (tree: EstimateTreeDto, rate?: number | null) =>
  tree.operations.reduce((sum, op) => sum + laborMinutes(op, rate), 0);

/**
 * TimeDisplay.kt tieOut: the parent rounds from its own exact value (or takes
 * `parent`, fixed by the level above), each child rounds from its own, and the
 * largest child takes the difference so the children add up to the parent.
 */
export const tieOut = (childExact: number[], parent?: number): [number, number[]] => {
  const total = parent ?? Math.round(childExact.reduce((a, b) => a + b, 0));
  if (childExact.length === 0) return [total, []];
  const children = childExact.map(Math.round);
  const diff = total - children.reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    let largest = 0;
    childExact.forEach((v, i) => { if (v > childExact[largest]) largest = i; });
    children[largest] += diff;
  }
  return [total, children];
};

/** Wall-clock minutes for the job, each work item and each operation, tied out. */
export interface WallTimes {
  job: number;
  items: Map<number, number>;
  operations: Map<number, number>;
}

export const wallTimes = (trees: EstimateTreeDto[], crewSize: number, rate?: number | null): WallTimes => {
  const crew = Math.max(1, crewSize);
  const opExact = trees.map(tree => tree.operations.map(op => laborMinutes(op, rate) / crew));
  const [job, treeShown] = tieOut(opExact.map(ops => ops.reduce((a, b) => a + b, 0)));
  const items = new Map<number, number>();
  const operations = new Map<number, number>();
  trees.forEach((tree, i) => {
    items.set(tree.id, treeShown[i]);
    const [, opsShown] = tieOut(opExact[i], treeShown[i]);
    tree.operations.forEach((op, j) => operations.set(op.id, opsShown[j]));
  });
  return {job, items, operations};
};

// ---- Distribution (PriceDistribution.kt) -------------------------------------------------

/** Floors each share, then gives the units left to the largest fractional parts, so the result sums to `total`. */
const largestRemainder = (exact: number[], total: number): number[] => {
  const floors = exact.map(Math.floor);
  let leftover = total - floors.reduce((a, b) => a + b, 0);
  exact.map((v, i) => i)
    .sort((a, b) => (exact[b] - floors[b]) - (exact[a] - floors[a]) || a - b)
    .forEach(i => { if (leftover > 0) { floors[i]++; leftover--; } });
  return floors;
};

/** Scales `current` to sum to exactly `newTotal`, keeping proportions; an all-zero list splits evenly. */
export const distribute = (current: number[], newTotal: number): number[] => {
  if (current.length === 0) return [];
  const currentTotal = current.reduce((a, b) => a + b, 0);
  if (currentTotal === 0) {
    const share = Math.floor(newTotal / current.length);
    const remainder = newTotal - share * current.length;
    return current.map((_, i) => i < remainder ? share + 1 : share);
  }
  return largestRemainder(current.map(v => v * newTotal / currentTotal), newTotal);
};

/** SQ-402, a typed Job Total: whole-dollar prices scaled to sum to exactly `newTotal`. Zeros stay zero; all zeros split evenly. */
export const distributeDollars = (current: number[], newTotal: number): number[] => {
  if (current.length === 0) return [];
  const currentTotal = current.reduce((a, b) => a + b, 0);
  const exact = currentTotal <= 0
    ? current.map(() => newTotal / current.length)
    : current.map(v => v * newTotal / currentTotal);
  return largestRemainder(exact, newTotal);
};

// ---- Equipment -----------------------------------------------------------------------------

export const equipmentRow = (row: EstimateTreeEquipmentDto): EquipmentChargeRow => ({
  equipmentId: row.equipmentId,
  durationMinutes: row.durationMinutes,
  hourlyRate: row.hourlyRate,
  minimumBillableMinutes: row.minimumBillableMinutes,
  minimumCharge: row.minimumCharge ?? null,
  wholeDollars: row.wholeDollars,
});

/** What the row bills: its time at the rate locked when it was added (never the machine's rate today). */
export const rowCost = (row: EstimateTreeEquipmentDto) => lockedCost(equipmentRow(row));

export const equipmentName = (row: Pick<EstimateTreeEquipmentDto, 'equipmentNickname' | 'equipmentType'>) =>
  row.equipmentNickname?.trim() || row.equipmentType?.trim() || 'Equipment';

/** Each machine's top-up to its minimum charge (SQ-441), with its name, as Android lists them. */
export const minimumTopUps = (rows: EstimateTreeEquipmentDto[]) =>
  equipmentCharges(rows.map(equipmentRow)).map(charge => {
    const named = rows.find(r => r.equipmentId === charge.equipmentId && r.equipmentNickname?.trim());
    return {...charge, name: named ? equipmentName(named) : equipmentName(rows.find(r => r.equipmentId === charge.equipmentId)!)};
  });

// ---- Cost item prices (EstimateFooterView.kt) ---------------------------------------------------

/** Digits and commas, one decimal point, at most two digits after it. */
export const cleanCostItemPrice = (text: string) => {
  const kept = text.replace(/[^\d.,]/g, '');
  const point = kept.indexOf('.');
  if (point < 0) return kept;
  return kept.slice(0, point + 1) + kept.slice(point + 1).replace(/[.,]/g, '').slice(0, 2);
};

export const parseCostItemPrice = (text: string) => {
  const n = Number(text.replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export const formatCostItemPrice = (price: number) =>
  price.toLocaleString('en-US', price === Math.floor(price)
    ? {maximumFractionDigits: 0}
    : {minimumFractionDigits: 2, maximumFractionDigits: 2});
